import { capitalize, first, orderBy } from 'lodash';
import { FinanceProvider, FinanceResponseStatus } from '../constants';
import {
  FinanceApplication,
  FinanceResponse,
  Rate,
} from '../legos/financingTypes';
import { getTradeInAllowance, getTradeInPayoff } from '../dealer-utils';
import { ParseRatesResult, parseRates } from './parseRates';
import {
  ValidateResponseResult,
  validateResponse,
  validateLimits,
  validateGapPremium,
} from './validations';
import {
  ProductTotalsResult,
  calculateProductTotals,
  getSelectedProductsWithFinalPrice,
} from './productHelpers';
import { Deal, TradeIn } from '../legos/dealTypes';
import { AccessoryConfig } from '../legos/accessoryTypes';
import { Car } from '../legos/carTypes';
import { Dealer } from '../legos/dealerTypes';

export {
  getTradeInAllowance,
  getTradeInPayoff,
  parseRates,
  validateResponse,
  validateLimits,
  validateGapPremium,
  calculateProductTotals,
  getSelectedProductsWithFinalPrice,
  ValidateResponseResult,
};

export function isUpstartOffer(
  financeResponse: FinanceResponse,
  financeApplication?: FinanceApplication,
) {
  const application = financeResponse.FinanceApplication ?? financeApplication;
  return application?.provider === FinanceProvider.UPSTART;
}

export function isApproved(financeResponse: FinanceResponse) {
  return (
    !!financeResponse.status &&
    [FinanceResponseStatus.APPROVED, FinanceResponseStatus.BOOKED].includes(
      financeResponse.status,
    )
  );
}
export function isConditional(financeResponse: FinanceResponse) {
  return financeResponse.status === FinanceResponseStatus.CONDITION;
}
export function isCounter(financeResponse: FinanceResponse) {
  return financeResponse.status === FinanceResponseStatus.COUNTERED;
}
export function isDenied(financeResponse: FinanceResponse) {
  return financeResponse.status === FinanceResponseStatus.DENIED;
}

/**
 * Returns the status label for a finance response status.
 */
export function getStatusLabel(financeResponse: FinanceResponse) {
  return financeResponse.status === FinanceResponseStatus.COUNTERED
    ? 'Counter Offer'
    : capitalize(financeResponse.status);
}

/**
 * Finds the highest rate object based on finance amount, down payment, and term.
 *
 * This is used for when a finance response is returned as a counter offer - we
 * want to show the highest rate terms the customer qualifies for. The highest
 * rate terms are defined as the highest term, highest finance amount with the
 * lowest down payment amount.
 */
export function getMaxAllowableRate(rates: Rate[]) {
  return first(
    orderBy(
      rates,
      ['maxFinanceAmount', 'maxDownPaymentAmount', 'months'],
      ['desc', 'asc', 'desc'],
    ),
  );
}

/**
 * Calculates the net trade for a finance response by subtracting the total
 * trade in payoff from the total trade in allowance.
 */
export function calculateNetTrade(financeResponse: FinanceResponse) {
  const tradeIns = financeResponse.data?.tradeIns ?? [];
  const tradeInAllowance = getTradeInAllowance(tradeIns);
  const tradePayoff = getTradeInPayoff(tradeIns);
  const netTrade = tradeInAllowance - tradePayoff;

  return { tradeIns, netTrade };
}

/**
 * Returns whether a finance response is at an editable state.
 */
export function isResponseEditable(financeResponse?: FinanceResponse) {
  return !(financeResponse?.financingReviewedAt || financeResponse?.lockedAt);
}

export type NormalizedFinanceResponse = ParseRatesResult &
  ValidateResponseResult &
  ProductTotalsResult & {
    application?: FinanceApplication;
    response: FinanceResponse;
    isApproved: boolean;
    isConditional: boolean;
    isCounter: boolean;
    isDenied: boolean;
    isUpstartOffer: boolean;
    netTrade: number;
    tradeIns: TradeIn[];
    financeAmount: number;
    statusLabel?: string;
    maxAllowableRate?: Rate; // This is the highest available rate based on amount and term
  };

/**
 * Helper function to calculate common values from a finance response
 *
 * @deprecated use smaller helper functions instead:
 *   - isUpstartOffer
 *   - isApproved
 *   - isConditional
 *   - isCounter
 *   - isDenied
 *   - calculateProductTotals
 *   - calculateNetTrade
 *   - parseRates
 *   - getSelectedProductsWithFinalPrice
 *   - getStatusLabel
 *   - validateResponse
 *   - validateLimits
 *   - validateGapPremium
 */
export function normalizeFinanceResponse({
  financeResponse,
  financeApplication,
  accessoryConfig,
  car,
  deal,
  dealer,
  rates,
}: {
  financeResponse: FinanceResponse;
  financeApplication?: FinanceApplication;
  accessoryConfig?: AccessoryConfig | null;
  car?: Car | null;
  deal?: Deal | null;
  calculatedBackendAmount?: number;
  dealer?: Dealer | null;
  rates?: Rate[];
}): NormalizedFinanceResponse {
  const productTotals = calculateProductTotals({
    financeResponse,
    accessoryConfig,
    car,
    deal,
  });

  const { netTrade, tradeIns } = calculateNetTrade(financeResponse);

  const financeAmount = financeResponse.data?.financeAmount ?? 0;
  const downPayment = financeResponse.data?.downPayment ?? 0;
  const allRates = rates ?? financeResponse.data?.rates ?? [];
  const parsedRates = parseRates({
    rates: allRates,
    downPayment,
    financeAmount,
    months: financeResponse.data?.months,
  });

  const statusLabel = getStatusLabel(financeResponse);
  const maxAllowableRate = getMaxAllowableRate(allRates);

  const validations =
    financeResponse.validations ??
    validateResponse({
      financeResponse,
      rates,
      selectedProducts: productTotals.selectedProducts,
      dealer,
    });

  return {
    ...parsedRates,
    ...validations,
    ...productTotals,
    isUpstartOffer: isUpstartOffer(financeResponse, financeApplication),
    isApproved: isApproved(financeResponse),
    isConditional: isConditional(financeResponse),
    isCounter: isCounter(financeResponse),
    isDenied: isDenied(financeResponse),
    application: financeApplication,
    response: financeResponse,
    netTrade,
    tradeIns,
    financeAmount,
    statusLabel,
    maxAllowableRate,
  };
}
